import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          if (!cancelled) setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          if (!cancelled) setState("error");
          return;
        }
      } catch (e) { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { if (!cancelled) setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-sm w-full text-center animate-fade-up" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 brand-text animate-spin mx-auto" />
            <h1 className="text-xl font-bold font-display mt-4">{t("billing.verifying")}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t("billing.verifyingDesc")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl brand-bg mb-2">
              <Crown className="h-8 w-8 text-white" />
            </div>
            <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <h1 className="text-2xl font-bold font-display" data-testid="payment-success-title">{t("billing.successTitle")}</h1>
            <p className="text-muted-foreground mt-2">{t("billing.successDesc")}</p>
            <Button className="mt-6 brand-bg text-white w-full" onClick={() => navigate("/dashboard")} data-testid="payment-success-continue">
              {t("billing.backToDashboard")}
            </Button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="h-10 w-10 text-amber-500 mx-auto" />
            <h1 className="text-xl font-bold font-display mt-4">{t("billing.pendingTitle")}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t("billing.pendingDesc")}</p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/dashboard")} data-testid="payment-success-dashboard">
              {t("billing.backToDashboard")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
